import { useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useMatch,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  BookOpenCheck,
  Drama,
  FileText,
  Layers,
  MessagesSquare,
  Search,
  Settings,
  Video,
  type LucideProps,
} from 'lucide-react';
import { send } from '@/shared/messages';
import { HomeRoute } from './routes/Home';
import { CaptureRoute } from './routes/Capture';
import { ChapterRoute } from './routes/Chapter';
import { ChatRoute } from './routes/Chat';
import { QueueRoute } from './routes/Queue';
import { ScenarioRoute } from './routes/Scenario';
import { SearchRoute } from './routes/Search';
import { VideosRoute } from './routes/Videos';
import { ChapterPicker } from './components/ChapterPicker';
import { TestPicker } from './components/TestPicker';
import { ThemeToggle } from '@/components/ThemeToggle';
import { TabLink } from '@/components/Tabs';
import { IconButton } from '@/components/IconButton';
import { Button } from '@/components/Button';
// Pomodoro hidden — Tish reported it wasn't helpful. Components kept in tree
// for easy re-enable if we revisit; just don't render them.
// import { PomodoroHeader } from './components/PomodoroHeader';
// import { BreakOverlay } from './components/BreakOverlay';

// Capture + Videos drive a live LMS tab — they require chrome.tabs and
// host permissions, neither of which exist in the webapp. Hide those nav
// entries when running outside the extension.
const isExtension = typeof chrome !== 'undefined' && typeof chrome.tabs !== 'undefined';

// Where "/" and cleared selections land, per target. The extension opens on
// the Today dashboard; the webapp (where chat is the main surface) opens on
// chat — Today stays one tab away in both.
const homePath = isExtension ? '/home' : '/chat';

interface NavItem {
  to: string;
  label: string;
  /** Shorter label for the cramped bottom bar (e.g. "Chapter text" → "Text"). */
  shortLabel?: string;
  icon: React.ComponentType<LucideProps>;
  /** Small mono count badge (cards due). Hidden when undefined or 0. */
  count?: number;
}

// The webapp injects its Settings page here (`settingsElement`) so it renders
// inside this shell and keeps the nav header; the extension leaves it unset
// and opens the options page instead.
export function App({ settingsElement }: { settingsElement?: React.ReactNode } = {}) {
  const navigate = useNavigate();
  // The active chapter is whatever the URL says. Matches the absolute path in
  // both targets (MemoryRouter and the webapp's nested BrowserRouter alike).
  const chapterMatch = useMatch('/chapter/:chapterId');
  const activeChapterId = chapterMatch?.params.chapterId ?? null;
  const [activeTestId, setActiveTestId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ready, setReady] = useState<boolean | null>(null);
  // Due-card count for the Cards tab chip; refreshed with the test scope.
  const [dueCount, setDueCount] = useState(0);

  useEffect(() => {
    if (!activeTestId) {
      setDueCount(0);
      return;
    }
    void send({ type: 'LIST_TESTS' }).then((r) => {
      setDueCount(r.tests.find((t) => t.id === activeTestId)?.dueCount ?? 0);
    });
  }, [activeTestId, refreshKey]);

  // Open a chapter, optionally with a search term to highlight on arrival
  // (carried as ?highlight= and consumed by the chapter route).
  function openChapter(id: string, highlight?: string) {
    const qs = highlight ? `?highlight=${encodeURIComponent(highlight)}` : '';
    navigate(`/chapter/${id}${qs}`);
  }

  function onCaptured(id: string) {
    setRefreshKey((k) => k + 1);
    openChapter(id);
  }

  // Settings lives on the options page in the extension, but is an in-shell
  // route in the webapp so the nav header stays visible.
  function openSettings() {
    if (settingsElement) {
      navigate('/settings');
      return;
    }
    chrome.runtime.openOptionsPage();
  }

  // Load the active test once on mount so the chapter list and capture
  // target are scoped from the first render. The persisted id can be stale —
  // e.g. a library re-import regenerates test UUIDs server-side, leaving
  // localStorage pointing at a test that no longer exists (which 404'd the
  // Today dashboard). Always validate it against the real list and fall back
  // to the first test, persisting the correction so both surfaces agree.
  useEffect(() => {
    (async () => {
      const [{ activeTestId: stored }, { tests }] = await Promise.all([
        send({ type: 'GET_ACTIVE_TEST' }),
        send({ type: 'LIST_TESTS' }),
      ]);
      if (stored && tests.some((t) => t.id === stored)) {
        setActiveTestId(stored);
        return;
      }
      const first = tests[0];
      setActiveTestId(first?.id ?? null);
      void send({ type: 'SET_ACTIVE_TEST', testId: first?.id ?? null });
    })();
  }, []);

  // Switching the active test changes which chapters are in scope, so leave
  // any open chapter (it may belong to the old test), persist the choice, and
  // refresh dependent views.
  function changeActiveTest(id: string | null) {
    setActiveTestId(id);
    void send({ type: 'SET_ACTIVE_TEST', testId: id });
    setRefreshKey((k) => k + 1);
    if (chapterMatch) navigate(homePath);
  }

  useEffect(() => {
    async function refresh() {
      const r = await chrome.storage.local.get(['apiKey', 'settings']);
      const hasKey = typeof r.apiKey === 'string' && r.apiKey.length > 10;
      const backend = (r.settings as { llmBackend?: string } | undefined)?.llmBackend;
      // Local-shim backend doesn't need an API key, just a running shim.
      setReady(backend === 'local-shim' || hasKey);
    }
    void refresh();
    const listener = (changes: Record<string, chrome.storage.StorageChange>, area: string) => {
      if (area === 'local' && ('apiKey' in changes || 'settings' in changes)) {
        void refresh();
      }
    };
    chrome.storage.onChanged.addListener(listener);
    return () => chrome.storage.onChanged.removeListener(listener);
  }, []);

  if (ready === null) {
    return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  }
  if (!ready) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <h2 className="mb-2 text-lg font-semibold">Welcome</h2>
        <p className="mb-4 text-sm text-slate-600">
          Add your Anthropic API key to start, or switch to the local Claude Code
          shim in Options if you're iterating without burning credits.
        </p>
        <Button variant="primary" size="lg" onClick={() => chrome.runtime.openOptionsPage()}>
          Open Options
        </Button>
      </div>
    );
  }

  // Single source of truth for the primary nav, rendered as underline tabs in
  // the wide-screen header and as a thumb-reachable bottom bar on narrow
  // surfaces (mobile webapp + the extension side panel). Videos/Capture are
  // extension-only — they drive a live LMS tab.
  const navItems: NavItem[] = [
    { to: '/home', label: 'Today', icon: BookOpenCheck },
    ...(isExtension
      ? [
          { to: '/videos', label: 'Videos', icon: Video },
          { to: '/capture', label: 'Chapter text', shortLabel: 'Text', icon: FileText },
        ]
      : []),
    { to: '/chat', label: 'Chat', icon: MessagesSquare },
    { to: '/cards', label: 'Cards', icon: Layers, count: dueCount },
    { to: '/scenario', label: 'Scenario', icon: Drama },
    { to: '/search', label: 'Search', icon: Search },
  ];

  return (
    <div className="@container relative flex h-full flex-col">
      {/* Wide header (≥ @3xl ≈ 768px): full nav + test/chapter pickers inline.
          Below that the nav tabs, the 4-control TestPicker, and the chapter
          select can't share one row and wrap into an unusable stack — so narrow
          surfaces use the slim bar + bottom tab nav below instead. */}
      <header className="hidden flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-card px-3 py-2 @3xl:flex">
        <div className="flex items-center gap-0.5 text-sm">
          {navItems.map((it) => (
            <TabLink
              key={it.to}
              to={it.to}
              label={it.label}
              shortLabel={it.shortLabel}
              count={it.count}
              icon={<NavIcon icon={it.icon} />}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <TestPicker
            activeTestId={activeTestId}
            onChange={changeActiveTest}
            refreshKey={refreshKey}
          />
          <ChapterPicker
            activeChapterId={activeChapterId}
            activeTestId={activeTestId}
            isViewing={!!chapterMatch}
            onChange={(id) => navigate(id ? `/chapter/${id}` : homePath)}
            refreshKey={refreshKey}
          />
          <ThemeToggle />
          <IconButton
            label="Settings"
            size="sm"
            icon={<Settings size={16} strokeWidth={1.75} />}
            onClick={openSettings}
          />
        </div>
      </header>

      {/* Narrow top bar: active-test + chapter pickers, theme, settings. Primary
          nav lives in the bottom bar. Both pickers are compact (just selectors,
          no management buttons) so test + chapter share one row; chapters are
          also reachable by tapping into Today / Cards / Search. */}
      <header className="flex items-center gap-2 border-b border-slate-200 bg-card px-3 py-1.5 @3xl:hidden">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <TestPicker
              activeTestId={activeTestId}
              onChange={changeActiveTest}
              refreshKey={refreshKey}
              compact
            />
          </div>
          <div className="min-w-0 flex-1">
            <ChapterPicker
              activeChapterId={activeChapterId}
              activeTestId={activeTestId}
              isViewing={!!chapterMatch}
              onChange={(id) => navigate(id ? `/chapter/${id}` : homePath)}
              refreshKey={refreshKey}
              compact
            />
          </div>
        </div>
        <ThemeToggle />
        <IconButton
          label="Settings"
          size="sm"
          icon={<Settings size={16} strokeWidth={1.75} />}
          onClick={openSettings}
        />
      </header>

      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Navigate to={homePath} replace />} />
          <Route
            path="home"
            element={<HomeRoute activeTestId={activeTestId} refreshKey={refreshKey} />}
          />
          {isExtension && (
            <Route
              path="videos"
              element={<VideosRoute activeTestId={activeTestId} onCaptured={onCaptured} />}
            />
          )}
          {isExtension && (
            <Route
              path="capture"
              element={<CaptureRoute activeTestId={activeTestId} onCaptured={onCaptured} />}
            />
          )}
          <Route path="chat" element={<ChatRoute onOpenChapter={openChapter} />} />
          <Route path="chat/:chatId" element={<ChatRoute onOpenChapter={openChapter} />} />
          <Route
            path="cards"
            element={<QueueRoute activeTestId={activeTestId} refreshKey={refreshKey} />}
          />
          <Route path="scenario" element={<ScenarioRoute activeTestId={activeTestId} />} />
          <Route
            path="search"
            element={<SearchRoute activeTestId={activeTestId} onOpenChapter={openChapter} />}
          />
          <Route path="chapter/:chapterId" element={<ChapterRouteFromUrl />} />
          {settingsElement && <Route path="settings" element={settingsElement} />}
          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
      </main>

      {/* Bottom tab bar — narrow surfaces only. A flex sibling (not fixed) so it
          never overlaps the chat composer / card-rating row; the safe-area
          inset clears the iOS home indicator. */}
      <nav
        className="flex shrink-0 items-stretch border-t border-slate-200 bg-card @3xl:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {navItems.map((it) => (
          <BottomTab key={it.to} {...it} />
        ))}
      </nav>
    </div>
  );
}

// Bridges the URL (path param + ?highlight=) to the prop-driven ChapterRoute so
// that route component stays oblivious to routing.
function ChapterRouteFromUrl() {
  const { chapterId } = useParams();
  const [params] = useSearchParams();
  if (!chapterId) {
    return <div className="p-4 text-sm text-slate-500">No chapter selected.</div>;
  }
  return <ChapterRoute chapterId={chapterId} highlightQuery={params.get('highlight') ?? undefined} />;
}

// Nav glyphs render at 16px/1.75 stroke (design spec). Only used in the wide
// header, which has room for icon + label, so always shown.
function NavIcon({ icon: Icon }: { icon: React.ComponentType<LucideProps> }) {
  return <Icon size={16} strokeWidth={1.75} className="shrink-0" />;
}

// Bottom-bar tab for narrow surfaces: stacked icon over label, brand-colored
// when active (keys off NavLink's aria-current, same as TabLink). The Cards due
// count rides as a small badge on the icon.
function BottomTab({ to, label, shortLabel, icon: Icon, count }: NavItem) {
  return (
    <NavLink
      to={to}
      className={[
        'group relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5',
        'text-slate-500 transition-colors duration-200 ease-out',
        'aria-[current=page]:text-blue-700',
      ].join(' ')}
    >
      <span className="relative inline-flex">
        <Icon size={20} strokeWidth={1.75} />
        {typeof count === 'number' && count > 0 && (
          <span className="absolute -right-2 -top-1.5 min-w-[15px] rounded-full bg-blue-600 px-1 text-center font-mono text-[9px] font-semibold leading-[15px] text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </span>
      <span className="text-[10px] font-medium leading-none">{shortLabel ?? label}</span>
    </NavLink>
  );
}
